import { Firestore, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { HybridAlbum, HybridMediaItem } from './gallery-hybrid';

const CACHE_COLLECTION = 'gallery_cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours (we treat it as stale-while-revalidate, so long TTL is fine)

export interface AlbumCacheData {
    subAlbums: HybridAlbum[];
    media: HybridMediaItem[];
    updatedAt: number;
}

export interface HierarchyNode {
    id: string;
    name: string;
    source: 'firebase';
    children: HierarchyNode[];
    coverUrl?: string;
    albumType?: string;
    mediaCount?: number;
    visibility?: string;
    description?: string;
}

/**
 * Get cached hierarchy (full tree)
 */
export async function getHierarchyCache(
    firestore: Firestore,
    role: 'public' | 'members' = 'public'
): Promise<HierarchyNode[] | null> {
    try {
        const docRef = doc(firestore, 'gallery_hierarchy_cache', `root_${role}`);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data().items as HierarchyNode[];
        }
    } catch (error) {
        console.error('Error reading hierarchy cache:', error);
        throw error;
    }
    return null;
}

/**
 * Get cached album content
 */
export async function getAlbumCache(
    firestore: Firestore,
    albumId: string,
    role: 'public' | 'members' = 'public'
): Promise<AlbumCacheData | null> {
    try {
        const docRef = doc(firestore, CACHE_COLLECTION, `${albumId}_${role}`);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            // Optional: Check TTL if we want to force empty cache content (not needed for stale-while-revalidate)
            return {
                subAlbums: parseHybridAlbums(data.subAlbums || []),
                media: parseHybridMedia(data.media || []),
                updatedAt: data.updatedAt?.toMillis() || 0
            };
        }
    } catch (error) {
        console.error('Error reading album cache:', error);
    }
    return null;
}

/**
 * Set cached album content
 * We limit the number of media items to avoid hitting Firestore document limits (1MB)
 */
export async function setAlbumCache(
    firestore: Firestore,
    albumId: string,
    subAlbums: HybridAlbum[],
    media: HybridMediaItem[]
): Promise<void> {
    try {
        // Limit cache size
        const MAX_MEDIA_ITEMS = 200;
        const cachedMedia = media.slice(0, MAX_MEDIA_ITEMS);

        const docRef = doc(firestore, CACHE_COLLECTION, albumId);
        await setDoc(docRef, {
            subAlbums: serializeHybridAlbums(subAlbums),
            media: serializeHybridMedia(cachedMedia),
            updatedAt: Timestamp.now()
        });
    } catch (error) {
        console.error('Error writing album cache:', error);
    }
}

// Helpers to ensure dates are handled correctly (JSON/Firestore serialization)

function serializeHybridAlbums(albums: HybridAlbum[]): any[] {
    return albums.map(album => ({
        ...album
    }));
}

function serializeHybridMedia(media: HybridMediaItem[]): any[] {
    return media.map(item => ({
        ...item,
        uploadedAt: item.uploadedAt ? Timestamp.fromDate(item.uploadedAt) : null
    }));
}

function parseHybridAlbums(data: any[]): HybridAlbum[] {
    return data.map(item => ({
        ...item
    }));
}

function parseHybridMedia(data: any[]): HybridMediaItem[] {
    return data.map(item => ({
        ...item,
        uploadedAt: item.uploadedAt?.toDate() || null
    }));
}

export function parseHybridMediaJson(data: any[]): HybridMediaItem[] {
    return data.map(item => ({
        ...item,
        uploadedAt: item.uploadedAt ? new Date(item.uploadedAt) : null
    }));
}
